import { useField } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronDownIcon, CircleUserIcon } from '@treams/treams-icons';
import { NoteFragment, useViewerWithManagersAndManageesQuery } from '@treams/treams-network';
import {
	Button,
	IconButton,
	Modal,
	Paragraph,
	Popover,
	UserRow,
	UserSelect,
} from '@treams/treams-ui';

export default function SubjectInput({ note }: { note: NoteFragment }) {
	const { t } = useTranslation();
	const [visible, setVisible] = useState(false);

	const [subjectID, , { setValue: setSubjectID }] = useField<string>('subjectID');
	const [confirmSubjectID, setConfirmSubjectID] = useState<string | undefined>(undefined);
	const loading = subjectID.value !== note.subject?.id;

	function confirmSubjectIfNeeded(newUserID: string) {
		const isSharedWithSubject = note.collaborators.edges.some(
			(edge) => edge.node.id === subjectID.value,
		);

		if (!isSharedWithSubject) {
			setSubjectID(newUserID);
			return;
		}

		setConfirmSubjectID(newUserID);
	}

	function closeConfirmationModal() {
		setConfirmSubjectID(undefined);
	}

	const { data } = useViewerWithManagersAndManageesQuery({
		fetchPolicy: 'cache-first',
	});

	return (
		<>
			<Popover.Target
				visible={visible}
				contents={
					<UserSelect.Menu
						excluded={note.subject ? [note.subject.id] : []}
						setSelected={(newUserID) => {
							setVisible(false);
							confirmSubjectIfNeeded(newUserID);
						}}
						searchPlaceholder={t('oneOnOne.meta.searchNewSubject')}
						suggestGroups={[
							{
								title: t('oneOnOne.meta.subjectGroups.managees'),
								userIDs: new Set(data?.viewer.managees.map((managee) => managee.id)),
							},
							{
								title: t('oneOnOne.meta.subjectGroups.managers'),
								userIDs: new Set(data?.viewer.managers.map((manager) => manager.source.id)),
							},
						]}
					/>
				}
				onDismiss={() => setVisible(false)}
				autofocus={true}
				style={{ width: 'max-content' }}
			>
				{note.subject ? (
					<UserRow
						user={note.subject}
						supertitle={t('oneOnOne.meta.subjectLabelLong')}
						preventContentGrow
						width="max-content"
					>
						{note.viewerCanUpdate && (
							<IconButton
								type="plain"
								icon={<ChevronDownIcon />}
								onClick={() => setVisible(true)}
								loading={loading}
								small
							/>
						)}
					</UserRow>
				) : (
					<Button
						type="transparent"
						title={t('oneOnOne.meta.addSubject')}
						icon={<CircleUserIcon />}
						onClick={() => setVisible(true)}
						small
					/>
				)}
			</Popover.Target>
			<Popover center visible={!!confirmSubjectID}>
				<Modal>
					<Modal.Header title={t('common.areYouSure')} onClose={closeConfirmationModal} />
					<Modal.Contents>
						<Paragraph>
							{t('oneOnOne.meta.sharedWithSubject', {
								subject: note.subject?.firstName,
							})}
						</Paragraph>
					</Modal.Contents>
					<Modal.FormFooter
						onCancel={closeConfirmationModal}
						onSave={() => {
							if (!confirmSubjectID) return;
							setSubjectID(confirmSubjectID);
							closeConfirmationModal();
						}}
						saveTitle={t('oneOnOne.meta.switchSubjectAnyway')}
					/>
				</Modal>
			</Popover>
		</>
	);
}
